import { useQuery } from "@tanstack/react-query";

import {
  PortfolioHealthReasonings,
  PortfolioHealthScores,
  PortfolioHealthSubScores,
} from "./useGetPortfolioMatrix";
import { guacHttpClient } from "@/lib/http";
import { HttpResponse } from "@/types";

export interface PortfolioJobHealthOverview {
  score: number | null;
  health: PortfolioHealthScores | null;
  healthSubScores: PortfolioHealthSubScores | null;
  healthReasons: PortfolioHealthReasonings | null;
  computedAt: string | null;
}

type RawPortfolioJobHealthOverview = Partial<{
  score: number | null;
  overallScore: number | null;
  health: PortfolioHealthScores | null;
  jobHealth: {
    computedAt?: string | null;
    reasonings?: unknown;
    scores?: PortfolioHealthScores | null;
  } | null;
  scores: PortfolioHealthScores | null;
  healthSubScores: PortfolioHealthSubScores | null;
  subScores: PortfolioHealthSubScores | null;
  healthReasons: unknown;
  reasonings: unknown;
  reasons: unknown;
  computedAt: string | null;
}>;

export const portfolioJobHealthQueryKey = (jobId: string) =>
  ["portfolio", "job-health", jobId] as const;

const getAverageScore = (scores: PortfolioHealthScores | null) => {
  if (!scores) return null;

  const values = Object.values(scores);
  if (!values.length) return null;

  return Math.round(
    values.reduce((total, score) => total + score, 0) / values.length,
  );
};

const normalizeReasonKey = (key: string) => {
  const normalized = key.trim().toLowerCase();
  if (normalized === "volume") return "volume";
  if (normalized === "velocity") return "velocity";
  if (normalized === "quality") return "quality";
  if (normalized === "engagement") return "engagement";
  return null;
};

const parseReasonings = (reasonings: unknown): PortfolioHealthReasonings | null => {
  if (!reasonings) return null;

  if (typeof reasonings === "string") {
    try {
      return parseReasonings(JSON.parse(reasonings) as unknown);
    } catch {
      return null;
    }
  }

  if (typeof reasonings !== "object") return null;

  const parsed: PortfolioHealthReasonings = {};
  for (const [key, value] of Object.entries(reasonings)) {
    const category = normalizeReasonKey(key);
    if (!category || typeof value !== "string") continue;
    parsed[category] = value;
  }

  return Object.keys(parsed).length ? parsed : null;
};

const normalizePortfolioJobHealth = (
  data: RawPortfolioJobHealthOverview,
): PortfolioJobHealthOverview => {
  const health = data.health ?? data.scores ?? data.jobHealth?.scores ?? null;

  return {
    score: data.score ?? data.overallScore ?? getAverageScore(health),
    health,
    healthSubScores: data.healthSubScores ?? data.subScores ?? null,
    healthReasons: parseReasonings(
      data.healthReasons ?? data.reasonings ?? data.reasons ?? data.jobHealth?.reasonings,
    ),
    computedAt: data.computedAt ?? data.jobHealth?.computedAt ?? null,
  };
};

export function useGetPortfolioJobHealth({
  enabled = true,
  jobId,
}: {
  enabled?: boolean;
  jobId: string;
}) {
  return useQuery({
    enabled,
    queryKey: portfolioJobHealthQueryKey(jobId),
    queryFn: async () => {
      const res = await guacHttpClient.get<
        RawPortfolioJobHealthOverview,
        HttpResponse<RawPortfolioJobHealthOverview>
      >(`portfolio/${jobId}/health`);

      return normalizePortfolioJobHealth(res.data);
    },
  });
}
